import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface User {
  id: string;
  username: string;
  name: string;
  role: string;
  permissions: string[];
}

interface LoginResponse {
  success: boolean;
  token: string;
  user: User;
}

interface ConfigResponse {
  environment: string;
  showDemoCredentials: boolean;
}

interface LoginProps {
  onLogin: (user: User, token: string) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDemoCredentials, setShowDemoCredentials] = useState(false);

  // Fetch environment configuration on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get<ConfigResponse>('/api/config');
        setShowDemoCredentials(response.data.showDemoCredentials);
      } catch (error) {
        // Default to not showing credentials if config fetch fails
        console.error('Failed to fetch config:', error);
        setShowDemoCredentials(false);
      }
    };
    fetchConfig();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post<LoginResponse>('/api/auth/login', {
        username,
        password
      });

      const { token, user } = response.data;
      onLogin(user, token);
    } catch (error: any) {
      console.error('Login error:', error);
      setError(
        error.response?.data?.error || 'Login failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1>Business Rules Engine</h1>
        <h2>Login</h2>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleLogin} className="login-form">
          <div className="form-group">
            <label htmlFor="username">Username:</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password:</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <button 
            type="submit" 
            className="login-button"
            disabled={loading}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        {showDemoCredentials && (
          <div className="demo-accounts">
            <h3>Demo Accounts:</h3>
            <ul>
              <li><strong>admin</strong> / admin123 - Full access</li>
              <li><strong>editor</strong> / editor123 - Create/edit rules</li>
              <li><strong>viewer</strong> / viewer123 - View/execute only</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;